import React, { useState } from "react";
import { Link, useLoaderData, useSearchParams } from "react-router-dom";
import CustomTooltip from "../Library/Tooltip";
import { formatThreeQuestions } from "./CrohnsFormLoader";

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text);
}

function CrohnsForm() {
  const [searchParams] = useSearchParams();
  const isDemo = Boolean(searchParams.get("demo")) || false;
  React.useEffect(() => {
    if (isDemo) {
      const htmlElement = document.querySelector("html")!;
      const previousTheme = htmlElement.getAttribute("data-theme");
      htmlElement.setAttribute("data-theme", "light");
      return () => {
        if (previousTheme) {
          htmlElement.setAttribute("data-theme", previousTheme);
        } else {
          htmlElement.removeAttribute("data-theme");
        }
      };
    }
  }, [isDemo]);
  const modalRef = React.useRef<HTMLDialogElement | null>(null); // { current: null }

  const data = useLoaderData() as formatThreeQuestions;
  const questions = data.questions;
  const treatments = data.treatments;
  const medications = data.medications;

  const [recomendation, setRecomendation] = useState("");
  const [selectedQuestions, setSelectedQuestions] = useState<string[]>([]);

  const getTreatmentsScores = () => {
    const scores: { [x: string]: number } = Object.keys(treatments).reduce(
      (acc, treatment) => ({ ...acc, [treatment]: 0 }),
      {}
    );
    for (const selectedQuestion of selectedQuestions) {
      const questionScoreInfo = questions[selectedQuestion];
      for (const [treatment, treatmentScore] of Object.entries(
        questionScoreInfo
      )) {
        scores[treatment] += treatmentScore;
      }
    }
    return scores;
  };
  const getRecommendation = () => {
    const treatmentScores = getTreatmentsScores();
    const maxScore = Math.max(...Object.values(treatmentScores));
    const recomendedTreatments = Object.entries(treatmentScores)
      .filter(([_treatment, score]) => score === maxScore)
      .map(([treatment]) => treatment);
    const recommendedMedications = recomendedTreatments
      .map((recomendation) => treatments[recomendation])
      .reduce((acc, medications) => [...acc, ...medications]);
    const medicationsDetails = Object.entries(medications).map(
      ([medication, details]) =>
        `${medication} (Route: ${details.route}):
        \nInduction dose - ${details.induction_dose},
        \nMaintenance dose - ${details.maintenance_dose}\n `
    );

    return `Recommended Treatments: ${recomendedTreatments.join(" ")}
    \nRecommended Medications: ${recommendedMedications.join(" ")}
    \nMedication Details: ${medicationsDetails.join("\n")}`;
  };

  return (
    <>
      <h1 className="text-3xl font-bold">Crohns Decision Support Tool</h1>
      <div className="overflow-x-auto">
        <table className="table table-zebra my-8 max-w-3xl">
          <thead>
            <tr>
              <th>Question</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(questions).map(([question], index) => (
              <tr key={index}>
                <td>{question}</td>
                <td>
                  <input
                    id={question}
                    type="checkbox"
                    // checked={hasAgreed}
                    className="checkbox"
                    onChange={(event) => {
                      // setHasAgreed(event.target.checked);
                      if (event.target.checked) {
                        setSelectedQuestions([...selectedQuestions, question]);
                      } else {
                        setSelectedQuestions(
                          selectedQuestions.filter((q) => q !== question)
                        );
                      }
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button
        className="btn"
        onClick={() => {
          setRecomendation(getRecommendation());
          modalRef.current?.showModal();
        }}
      >
        Get Scores
      </button>
      {!isDemo && (
        <Link to={"/"} className="btn btn-link">
          Back
        </Link>
      )}
      {
        <dialog ref={modalRef} className="modal">
          <div className="modal-box w-11/12 max-w-5xl">
            <h3 className="font-bold text-lg">Treatment Recommendations</h3>
            <p
              className="py-4 whitespace-break-spaces"
              // whitespace-pre-line"
              style={{
                textAlign: "left",
                fontFamily: "monospace,serif",
                fontSize: "0.75em",
              }}
            >
              {/* Press ESC key or click the button below to close */}
              {recomendation}
            </p>
            <div className="modal-action">
              <CustomTooltip content="Copied!" trigger="click">
                <button
                  className="btn"
                  onClick={() => copyToClipboard(recomendation || "")}
                >
                  Copy
                </button>
              </CustomTooltip>
              <form method="dialog">
                {/* if there is a button in form, it will close the modal */}
                <button className="btn">Close</button>
              </form>
            </div>
          </div>
        </dialog>
      }
    </>
  );
}

export default CrohnsForm;
